import { debounce } from '@cometchat/uikit-elements';
import { Close, ExpandMore, Search } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
    Popover,
    Stack,
    Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Context } from '../../../../App';
import { companyListRecordApi, customerListRecordApi } from '../../../../redux/Invoice/action';
import useRecordPaymentStore from '../../../../store/UseRecordPaymentStore';

const styles = {
    menuPadding: {
        padding: '0',
        width: 'calc(100% + 10px)!important',
    },
    formControlLabel: {
        padding: '6px 16px',
        marginRight: '0!important',
        width: '100%',
    },
};

const RecordPaymentListFilters = ({ recordPaymentParams, setRecordPaymentParams, customerDetail, setCustomerDetail }) => {
    const dispatch = useDispatch();
    const { isOpenSideBar } = useContext(Context);

    const [anchorElDate, setAnchorElDate] = useState(null);
    const openDate = Boolean(anchorElDate);

    const [anchorElCustomer, setAnchorElCustomer] = useState(null);
    const openCustomer = Boolean(anchorElCustomer);

    const [anchorElCompany, setAnchorElCompany] = useState(null);
    const openCompany = Boolean(anchorElCompany);

    const [anchorElStatus, setAnchorElStatus] = useState(null);
    const openStatus = Boolean(anchorElStatus);

    // Local state for date values (before applying to store)
    const [localFromDate, setLocalFromDate] = useState(null);
    const [localToDate, setLocalToDate] = useState(null);

    const [customerList, setCustomerList] = useState([]);
    const [companyList, setCompanyList] = useState([]);

    const activeFilterCount = [
        recordPaymentParams.fromDate,
        recordPaymentParams.toDate,
        recordPaymentParams.customer.length,
        recordPaymentParams.company.length,
        recordPaymentParams.status.length,
    ].filter((count) => count > 0).length;

    const showClearFilterButton = activeFilterCount > 0;
    const clearFilterLabel = activeFilterCount > 1 ? 'Clear Filters' : 'Clear Filter';

    const handleResetFilter = () => {
        setRecordPaymentParams({
            ...recordPaymentParams,
            fromDate: '',
            toDate: '',
            customer: [],
            company: [],
            status: [],
            page: 1,
        });
    };

    // Initialize local dates when menu opens
    useEffect(() => {
        if (openDate) {
            setLocalFromDate(recordPaymentParams.fromDate ? dayjs(recordPaymentParams.fromDate) : null);
            setLocalToDate(recordPaymentParams.toDate ? dayjs(recordPaymentParams.toDate) : null);
        }
    }, [openDate, recordPaymentParams.fromDate, recordPaymentParams.toDate]);
    useEffect(() => {
        getCustomerFilterList();
        getCompanyFilterList();
    }, []);

    const getCustomerFilterList = async () => {
        const resp = await dispatch(customerListRecordApi());
        setCustomerList(resp?.length > 0 ? resp : []);
    };

    const getCompanyFilterList = async () => {
        const resp = await dispatch(companyListRecordApi());
        setCompanyList(resp?.length > 0 ? resp : []);
    };
    const debounceSearch = useCallback(
        debounce((event) => {
            const latestParams = useRecordPaymentStore.getState().recordPaymentParams;
            setRecordPaymentParams({
                ...latestParams,
                searchText: event?.target?.value || '',
                page: 1,
            });
        }, 800),
        []
    );

    const handleFromDateChange = (newValue) => {
        setLocalFromDate(newValue);
    };

    const handleToDateChange = (newValue) => {
        setLocalToDate(newValue);
    };

    const handleApply = () => {
        setRecordPaymentParams({
            ...recordPaymentParams,
            fromDate: localFromDate ? dayjs(localFromDate).format('YYYY-MM-DD') : '',
            toDate: localToDate ? dayjs(localToDate).format('YYYY-MM-DD') : '',
            page: 1,
        });
        setAnchorElDate(null);
    };

    const handleClear = () => {
        setLocalFromDate(null);
        setLocalToDate(null);
        setRecordPaymentParams({
            ...recordPaymentParams,
            fromDate: '',
            toDate: '',
            page: 1,
        });
        setAnchorElDate(null);
    };

    const getDateButtonText = () => {
        const fromDate = recordPaymentParams.fromDate;
        const toDate = recordPaymentParams.toDate;

        if (fromDate && toDate) {
            if (fromDate === toDate) {
                return dayjs(fromDate).format('MMM DD, YYYY');
            }
            return `${dayjs(fromDate).format('MMM DD')} - ${dayjs(toDate).format('MMM DD, YYYY')}`;
        } else if (fromDate) {
            return `From ${dayjs(fromDate).format('MMM DD, YYYY')}`;
        } else if (toDate) {
            return `To ${dayjs(toDate).format('MMM DD, YYYY')}`;
        }
        return 'Date';
    };

    // Handle customer selection (multiple)
    const handleCustomerChange = (customer) => {
        const currentCustomers = recordPaymentParams.customer || [];
        let updatedCustomers;

        if (currentCustomers.includes(customer?._id)) {
            // Remove if already selected
            updatedCustomers = currentCustomers.filter((id) => id !== customer?._id);
        } else {
            // Add if not selected
            updatedCustomers = [...currentCustomers, customer?._id];
        }
        console.log('🚀 ~ handleCustomerChange ~ customer:', customer);
        setCustomerDetail(customer);
        setRecordPaymentParams({
            ...recordPaymentParams,
            customer: updatedCustomers,
            page: 1,
        });
    };

    // Handle company selection (multiple)
    const handleCompanyChange = (companyId) => {
        const currentCompanies = recordPaymentParams.company || [];
        let updatedCompanies;

        if (currentCompanies.includes(companyId)) {
            // Remove if already selected
            updatedCompanies = currentCompanies.filter((id) => id !== companyId);
        } else {
            // Add if not selected
            updatedCompanies = [...currentCompanies, companyId];
        }
        setRecordPaymentParams({
            ...recordPaymentParams,
            company: updatedCompanies,
            page: 1,
        });
    };

    // Handle company selection (multiple)
    const handleStatusChange = (status) => {
        const currentStatus = recordPaymentParams.status || [];
        let updatedStatus;

        if (currentStatus.includes(status)) {
            // Remove if already selected
            updatedStatus = currentStatus.filter((id) => id !== status);
        } else {
            // Add if not selected
            updatedStatus = [...currentStatus, status];
        }

        setRecordPaymentParams({
            ...recordPaymentParams,
            status: updatedStatus,
            page: 1,
        });
    };

    // Get customer button text with count
    const getCustomerButtonText = () => {
        const selectedCount = recordPaymentParams.customer?.length || 0;
        if (selectedCount > 0) {
            return `${selectedCount} Customer`;
        }
        return 'CUSTOMER';
    };

    // Get company button text with count
    const getCompanyButtonText = () => {
        const selectedCount = recordPaymentParams.company?.length || 0;
        if (selectedCount > 0) {
            return `${selectedCount} Company`;
        }
        return 'COMPANY';
    };

    // Get status button text with count
    const getStatusButtonText = () => {
        const selectedCount = recordPaymentParams.company?.length || 0;
        if (selectedCount > 0) {
            return `${selectedCount} Status`;
        }
        return 'STATUS';
    };

    return (
        <Grid container display={'flex'} justifyContent={'space-between'} alignItems={'center'} marginTop={'48px'}>
            {/* Menus Filter  */}
            <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                lg={isOpenSideBar ? 'auto' : 8}
                xl={7.5}
                justifyContent={'flex-start'}
                alignItems={'center'}
                rowGap={1}
                columnGap={2}
            >
                {/* Date Filter */}
                <Grid item md="auto">
                    <Button
                        variant={recordPaymentParams.fromDate || recordPaymentParams.toDate ? 'contained' : 'text'}
                        id="date-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElDate(event.currentTarget)}
                        sx={{
                            color: recordPaymentParams.fromDate || recordPaymentParams.toDate ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        endIcon={<ExpandMore />}
                    >
                        {getDateButtonText()}
                    </Button>

                    {/* Use Popover instead of Menu */}
                    <Popover
                        id="date-filter-popover"
                        anchorEl={anchorElDate}
                        open={openDate}
                        onClose={() => setAnchorElDate(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{
                            style: {
                                minWidth: '380px',
                                maxWidth: '400px',
                            },
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ p: 0 }}>
                                {/* Dates in a row */}
                                <Box sx={{ display: 'flex', gap: 2, padding: 2 }}>
                                    <Stack direction={'column'}>
                                        <Typography className="formLabelTitle" mb={1}>
                                            From
                                        </Typography>
                                        <DatePicker
                                            value={localFromDate}
                                            onChange={handleFromDateChange}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                },
                                            }}
                                        />
                                    </Stack>
                                    <Stack direction={'column'}>
                                        <Typography className="formLabelTitle" mb={1}>
                                            To
                                        </Typography>
                                        <DatePicker
                                            value={localToDate}
                                            onChange={handleToDateChange}
                                            minDate={localFromDate}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>

                                <Divider />

                                {/* Buttons */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 2, px: 1, gap: 1 }}>
                                    <Button variant="outlined" onClick={handleClear}>
                                        Clear
                                    </Button>
                                    <Button variant="contained" onClick={handleApply}>
                                        Apply
                                    </Button>
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </Popover>
                </Grid>

                {/* Customer Filter */}
                <Grid item md="auto">
                    <Button
                        variant={recordPaymentParams.customer?.length > 0 ? 'contained' : 'text'}
                        id="customer-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElCustomer(event.currentTarget)}
                        sx={{
                            color: recordPaymentParams.customer?.length > 0 ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        startIcon={
                            recordPaymentParams.customer?.length > 0 && (
                                <Avatar
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        fontSize: '14px!important',
                                        fontWeight: '500!important',
                                        letterSpacing: '0.4px',
                                        lineHeight: '24px',
                                        width: 24,
                                        height: 24,
                                    }}
                                >
                                    {recordPaymentParams.customer?.length}
                                </Avatar>
                            )
                        }
                        endIcon={<ExpandMore />}
                    >
                        {getCustomerButtonText()}
                    </Button>
                    <Menu
                        id="customer-menu"
                        anchorEl={anchorElCustomer}
                        open={openCustomer}
                        onClose={() => setAnchorElCustomer(null)}
                        MenuListProps={{
                            'aria-labelledby': 'customer-filter-button',
                        }}
                        PaperProps={{
                            style: {
                                maxHeight: '400px',
                            },
                        }}
                    >
                        {customerList?.length > 0 ? (
                            customerList.map((customer, i) => (
                                <MenuItem key={customer._id} sx={styles.menuPadding}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={recordPaymentParams.customer?.includes(customer._id) || false}
                                                onChange={() => handleCustomerChange(customer)} // handle only once
                                            />
                                        }
                                        label={customer.fullName}
                                        sx={styles.formControlLabel}
                                    />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem>No Customers Available</MenuItem>
                        )}
                    </Menu>
                </Grid>
                {/* Company Filter */}
                <Grid item md="auto">
                    <Button
                        variant={recordPaymentParams.company?.length > 0 ? 'contained' : 'text'}
                        id="company-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElCompany(event.currentTarget)}
                        sx={{
                            color: recordPaymentParams.company?.length > 0 ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        startIcon={
                            recordPaymentParams.company?.length > 0 && (
                                <Avatar
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        fontSize: '14px!important',
                                        fontWeight: '500!important',
                                        letterSpacing: '0.4px',
                                        lineHeight: '24px',
                                        width: 24,
                                        height: 24,
                                    }}
                                >
                                    {recordPaymentParams.company?.length}
                                </Avatar>
                            )
                        }
                        endIcon={<ExpandMore />}
                    >
                        {getCompanyButtonText()}
                    </Button>
                    <Menu
                        id="company-menu"
                        anchorEl={anchorElCompany}
                        open={openCompany}
                        onClose={() => setAnchorElCompany(null)}
                        MenuListProps={{
                            'aria-labelledby': 'company-filter-button',
                        }}
                        PaperProps={{
                            style: {
                                maxHeight: '400px',
                                // width: '320px',
                            },
                        }}
                    >
                        {companyList?.length > 0 ? (
                            companyList.map((company) => (
                                <MenuItem key={company._id} sx={styles.menuPadding}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={recordPaymentParams.company?.includes(company._id) || false}
                                                onChange={() => handleCompanyChange(company._id)}
                                            />
                                        }
                                        label={company.company}
                                        sx={styles.formControlLabel}
                                    />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem>No Data</MenuItem>
                        )}
                    </Menu>
                </Grid>
                {/* Status Filter */}
                <Grid item md="auto">
                    <Button
                        variant={recordPaymentParams.status?.length > 0 ? 'contained' : 'text'}
                        id="status-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElStatus(event.currentTarget)}
                        sx={{
                            color: recordPaymentParams.status?.length > 0 ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        startIcon={
                            recordPaymentParams.status?.length > 0 && (
                                <Avatar
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        fontSize: '14px!important',
                                        fontWeight: '500!important',
                                        letterSpacing: '0.4px',
                                        lineHeight: '24px',
                                        width: 24,
                                        height: 24,
                                    }}
                                >
                                    {recordPaymentParams.status?.length}
                                </Avatar>
                            )
                        }
                        endIcon={<ExpandMore />}
                    >
                        {getStatusButtonText()}
                    </Button>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorElStatus}
                        open={openStatus}
                        onClose={() => setAnchorElStatus(null)}
                        MenuListProps={{
                            'aria-labelledby': 'status-filter-button',
                        }}
                        PaperProps={{
                            style: {
                                maxHeight: '400px',
                            },
                        }}
                    >
                        {[
                            { _id: 'fullyPaid', label: 'Paid' },
                            { _id: 'remaining', label: 'Remaining' },
                            { _id: 'owing', label: 'Owing' },
                        ].map((status) => (
                            <MenuItem key={status._id} sx={styles.menuPadding}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={recordPaymentParams.status?.includes(status._id) || false}
                                            onChange={() => {
                                                handleStatusChange(status._id);
                                            }}
                                        />
                                    }
                                    label={status.label}
                                    sx={styles.formControlLabel}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </Grid>
                {/* Clear Filter */}
                {showClearFilterButton && (
                    <Grid item md="auto">
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Close />}
                            onClick={handleResetFilter}
                            sx={{
                                background: '#0000001A',
                                color: '#000000',
                                '&:hover': {
                                    background: '#0000001A',
                                    boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                },
                                boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                fontWeight: 500,
                                lineHeight: '24px',
                            }}
                        >
                            {clearFilterLabel}
                        </Button>
                    </Grid>
                )}
            </Grid>

            {/* Input Search  */}
            <Grid item xs={12} sm={12} mt={{ sm: 2, xs: 2, md: 2, lg: 0 }} md={12} lg={isOpenSideBar ? 3 : 4} xl={4.5} padding={'6px'}>
                <FormControl fullWidth>
                    <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                    <OutlinedInput
                        id="record-payment-search"
                        placeholder="Customer name, invoice #, company etc..."
                        startAdornment={
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        }
                        endAdornment={
                            recordPaymentParams.searchText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            setRecordPaymentParams({
                                                ...recordPaymentParams,
                                                searchText: '',
                                            });
                                            const search = document.getElementById('record-payment-search');
                                            if (search) {
                                                search.value = '';
                                            }
                                        }}
                                        edge="end"
                                        size="small"
                                    >
                                        <Close fontSize="14" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                        onChange={debounceSearch}
                        label="Search"
                    />
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default RecordPaymentListFilters;
